
import { Input } from "@/components/ui/input";
import { CalendarClockIcon, CalendarIcon, CheckIcon, ClipboardTypeIcon, HashIcon, ImageIcon, ListChecksIcon, PaperclipIcon, PlusIcon, SlidersHorizontalIcon, SmileIcon, SquareArrowDownIcon, SquareCheckIcon, TextIcon, ToggleRightIcon, TrashIcon, TypeIcon, XIcon } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { DateInput } from "@/components/ui/date-input";
import { DateTimePicker24hForm } from "@/components/ui/datetime";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState } from "react";
import { FileUploader } from "@/components/ui/file-uploader";
import { isAfter, isValid, subYears } from "date-fns";
import { FieldType, useFormContext } from "./context";
import { MinimalTiptapEditor } from "@/components/ui/minimal-tiptap";
import QuestionRenderer from "./(components)/question-renderer";
import { JSONContent } from "@tiptap/core";

// These will be available from the sidebar
export type FieldTypeName = "input" | "textarea" | "date" | "datetime" | "switch" | "checkbox" | "checklist" | "select" | "file" | "image" | "text" | "spacer" | "number" | "rating";

export const fields = [
  {
    type: "input",
    title: "Single line",
    icon: <TypeIcon className="w-4 h-4" />
  },
  {
    type: "textarea",
    title: "Multi line",
    icon: <TextIcon className="w-4 h-4" />
  },
  {
    type: "date",
    title: "Date",
    icon: <CalendarIcon className="w-4 h-4" />
  },
  {
    type: "datetime",
    title: "Date & Time",
    icon: <CalendarClockIcon className="w-4 h-4" />
  },
  {
    type: "switch",
    title: "Yes/no",
    icon: <ToggleRightIcon className="w-4 h-4" />
  },
  {
    type: "checkbox",
    title: "Checkbox",
    icon: <SquareCheckIcon className="w-4 h-4" />
  },
  {
    type: "checklist",
    title: "Checklist",
    icon: <ListChecksIcon className="w-4 h-4" />
  },
  {
    type: "select",
    title: "Select",
    icon: <SquareArrowDownIcon className="w-4 h-4" />
  },
  {
    type: "file",
    title: "Attachments",
    icon: <PaperclipIcon className="w-4 h-4" />
  },
  {
    type: "image",
    title: "Image",
    icon: <ImageIcon className="w-4 h-4" />
  },
  {
    type: "text",
    title: "Text content",
    icon: <ClipboardTypeIcon className="w-4 h-4" />
  },
  {
    type: "number",
    title: "Number",
    icon: <HashIcon className="w-4 h-4" />
  },
  {
    type: "rating",
    title: "1 to 5 Picker",
    icon: <SmileIcon className="w-4 h-4" />
  },
];

const Question = ({ field, preview, setQuestionForField }: { field: FieldType, preview: boolean, setQuestionForField: (id: string, question: JSONContent) => void }) => {
  return (
    <>
      {preview
        ?
        <QuestionRenderer field={field} />
        :
        <MinimalTiptapEditor
          editorContentClassName="p-0"
          className="p-0 border-none shadow-none"
          onChange={(v) => { setQuestionForField(field.id, v as JSONContent) }}
          value={field?.question}
          hideToolbar
          placeholder="Type your question here..."
          editable={!preview}
        // defaultFormatting={{
        //   node: { type: 'heading', attrs: { level: 2 } }
        // }}
        />
      }
    </>
  )
}

// These define how we render the field
export const renderers = {
  input: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, setFieldRequired, removeFieldById, updateFieldResponse, fieldResponses } = useFormContext();

    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || '') : '';

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">

            <div className="flex items-center gap-2">
              <button onClick={() => { removeFieldById(field.id) }} className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"><TrashIcon className="w-4 h-4" /></button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary"><TypeIcon className="w-4 h-4" /></div>
              <p className="text-lg font-semibold">Single line</p>
            </div>


            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch checked={field.required} onCheckedChange={(checked) => setFieldRequired(field.id, checked)} size="sm" id={`${field.id}-required`} />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <Input type="text" disabled={disabled || !preview} value={fieldResponse} onChange={(e) => { updateFieldResponse(field.id, e.currentTarget.value, field.type) }} />
      </div>
    )
  },
  textarea: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, setFieldRequired, removeFieldById, updateFieldResponse, fieldResponses } = useFormContext();

    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || '') : '';

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button onClick={() => { removeFieldById(field.id) }} className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"><TrashIcon className="w-4 h-4" /></button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary"><TextIcon className="w-4 h-4" /></div>
              <p className="text-lg font-semibold">Multi line</p>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch checked={field.required} onCheckedChange={(checked) => setFieldRequired(field.id, checked)} size="sm" id={`${field.id}-required`} />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <Textarea disabled={disabled || !preview} value={fieldResponse} onChange={(e) => { updateFieldResponse(field.id, e.currentTarget.value, field.type) }} />
      </div>
    )
  },
  date: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, setFieldRequired, removeFieldById, updateFieldResponse, fieldResponses } = useFormContext();

    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || '') : '';

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button onClick={() => { removeFieldById(field.id) }} className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"><TrashIcon className="w-4 h-4" /></button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary"><CalendarIcon className="w-4 h-4" /></div>
              <p className="text-lg font-semibold">Date</p>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch checked={field.required} onCheckedChange={(checked) => setFieldRequired(field.id, checked)} size="sm" id={`${field.id}-required`} />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <DateInput disabled={disabled || !preview} date={(fieldResponse && isValid(new Date(fieldResponse)) && isAfter(new Date(fieldResponse), subYears(new Date(), 100))) ? new Date(fieldResponse) : undefined} onDateChange={(date) => { updateFieldResponse(field.id, date?.toISOString() || '', field.type) }} />
      </div>
    )
  },
  datetime: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, setFieldRequired, removeFieldById, updateFieldResponse, fieldResponses } = useFormContext();

    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || '') : '';

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button onClick={() => { removeFieldById(field.id) }} className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"><TrashIcon className="w-4 h-4" /></button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary"><CalendarClockIcon className="w-4 h-4" /></div>
              <p className="text-lg font-semibold">Date & Time</p>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch checked={field.required} onCheckedChange={(checked) => setFieldRequired(field.id, checked)} size="sm" id={`${field.id}-required`} />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <DateTimePicker24hForm disabled={disabled || !preview} date={(fieldResponse && isValid(new Date(fieldResponse)) && isAfter(new Date(fieldResponse), subYears(new Date(), 100))) ? new Date(fieldResponse) : undefined} onDateChange={(date) => { updateFieldResponse(field.id, date?.toISOString() || '', field.type) }} />
      </div>
    )
  },
  switch: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, setFieldRequired, removeFieldById, updateFieldResponse, fieldResponses } = useFormContext();

    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || '') : '';

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button onClick={() => { removeFieldById(field.id) }} className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"><TrashIcon className="w-4 h-4" /></button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary"><ToggleRightIcon className="w-4 h-4" /></div>
              <p className="text-lg font-semibold">Yes/no</p>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch checked={field.required} onCheckedChange={(checked) => setFieldRequired(field.id, checked)} size="sm" id={`${field.id}-required`} />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <Switch disabled={disabled || !preview} checked={fieldResponse === 'true'} onCheckedChange={(checked) => { updateFieldResponse(field.id, checked ? 'true' : 'false', field.type) }} />
      </div>
    )
  },
  checkbox: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, setFieldRequired, removeFieldById, updateFieldResponse, fieldResponses } = useFormContext();

    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || '') : '';

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button onClick={() => { removeFieldById(field.id) }} className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"><TrashIcon className="w-4 h-4" /></button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary"><SquareCheckIcon className="w-4 h-4" /></div>
              <p className="text-lg font-semibold">Checkbox</p>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch checked={field.required} onCheckedChange={(checked) => setFieldRequired(field.id, checked)} size="sm" id={`${field.id}-required`} />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <Checkbox disabled={disabled || !preview} checked={fieldResponse === 'true'} onCheckedChange={(checked) => { updateFieldResponse(field.id, checked ? 'true' : 'false', field.type) }} />
      </div>
    )
  },
  checklist: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, addFieldOption, removeFieldOption, updateOptionText, setFieldRequired, removeFieldById, updateFieldResponse, fieldResponses } = useFormContext();

    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || '') : '';

    // Change value splitted by option id splitted by ;
    const handleChangeChecked = (option_id: string, checked: boolean) => {
      const splitted = fieldResponse.split(';');
      const indexOfValue = splitted.findIndex((value) => {
        const splittedValue = value.split(':');
        return splittedValue[0] === option_id;
      });
      if (indexOfValue === -1) {
        splitted.push(`${option_id}:${checked ? 'true' : 'false'}`);
      } else {
        splitted[indexOfValue] = `${option_id}:${checked ? 'true' : 'false'}`;
      }
      const newValue = splitted.join(';');
      updateFieldResponse(field.id, newValue, field.type);
    }

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button onClick={() => { removeFieldById(field.id) }} className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"><TrashIcon className="w-4 h-4" /></button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary"><ListChecksIcon className="w-4 h-4" /></div>
              <p className="text-lg font-semibold">Checklist</p>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch checked={field.required} onCheckedChange={(checked) => setFieldRequired(field.id, checked)} size="sm" id={`${field.id}-required`} />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <div className="flex flex-col gap-1">
          {field?.options?.map((option) => {
            const currentValue = fieldResponse.split(';').find((value) => {
              const splittedValue = value.split(':');
              return splittedValue[0] === option.id;
            });
            const isChecked = currentValue ? currentValue.split(':')[1] === 'true' : false;

            return (
              <div className="flex items-center gap-2" key={option.id}>
                <Checkbox disabled={disabled || !preview} checked={isChecked} onCheckedChange={(checked) => { handleChangeChecked(option.id, checked as boolean) }} />
                {preview ? <p>{option.text}</p> : <input autoFocus placeholder="Type your option here..." value={option.text || ''} onChange={(e) => updateOptionText(field.id, option.id, e.currentTarget.value)} className="w-full bg-transparent focus:outline-none" />}
                {!preview && <button onClick={() => { removeFieldOption(field.id, option.id) }} className="p-1 text-red-600 rounded-sm hover:bg-neutral-100"><XIcon className="w-3 h-3" /></button>}
              </div>
            )
          })}

          {!preview && <button onClick={() => { addFieldOption(field.id) }} className="flex items-center justify-center h-10 gap-1 text-sm font-medium border border-dashed rounded-md hover:bg-neutral-50 text-neutral-400 hover:text-neutral-600"><PlusIcon className="w-4 h-4" />Add option</button>}
        </div>
      </div>
    )
  },
  select: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, addFieldOption, removeFieldOption, setFieldRequired, removeFieldById, updateFieldResponse, fieldResponses } = useFormContext();

    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || '') : '';
    const selectedOption = fieldResponse.split(';').find(v => v.split(':')[1] === 'true')?.split(':')[0] || undefined

    // Change value splitted by option id splitted by ;
    const handleChangeChecked = (option_id: string, checked: boolean) => {
      let splitted = fieldResponse.split(';');

      const indexOfValue = splitted.findIndex((value) => {
        const splittedValue = value.split(':');
        return splittedValue[0] === option_id;
      });

      // Set all to false
      // splitted = splitted.map((v) => `${v.split(':')[0]}:false`)

      if (indexOfValue === -1) {
        // Set new value
        splitted.push(`${option_id}:${checked ? 'true' : 'false'}`);
      } else {
        // Set this option to true
        splitted[indexOfValue] = `${option_id}:${checked ? 'true' : 'false'}`;
      }

      const newValue = splitted.join(';');
      updateFieldResponse(field.id, newValue, field.type);
    }

    const [optionValue, setoptionValue] = useState('');

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button onClick={() => { removeFieldById(field.id) }} className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"><TrashIcon className="w-4 h-4" /></button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary"><SquareArrowDownIcon className="w-4 h-4" /></div>
              <p className="text-lg font-semibold">Select</p>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch checked={field.required} onCheckedChange={(checked) => setFieldRequired(field.id, checked)} size="sm" id={`${field.id}-required`} />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <div className="flex flex-col gap-1">
          <Select disabled={disabled || !preview} value={selectedOption} onValueChange={(value) => { handleChangeChecked(value, true) }}>
            <SelectTrigger>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {field?.options?.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!preview &&
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center justify-center h-10 gap-1 text-sm font-medium border border-dashed rounded-md hover:bg-neutral-50 text-neutral-400 hover:text-neutral-600"><SlidersHorizontalIcon className="w-4 h-4" />Manage options</button>
              </PopoverTrigger>
              <PopoverContent className="w-full">
                <div className="flex items-start h-full gap-4">

                  <div className="flex flex-col gap-2 min-w-60">
                    <div>
                      <p className="text-sm font-medium text-neutral-400">New option</p>
                      <Input value={optionValue} onChange={(e) => { setoptionValue(e.currentTarget.value) }} />
                    </div>
                    <Button onClick={() => { addFieldOption(field.id, optionValue); setoptionValue('') }}>Add option</Button>
                  </div>

                  <div className="min-w-60 max-h-96 overflow-auto">
                    <p className="text-sm font-medium text-neutral-400">Current options</p>
                    <div className="flex flex-col gap-2">
                      {field?.options?.map((option) => (
                        <div className="flex items-center justify-between h-10 px-3 py-2 border rounded-md" key={option.id}>
                          <p className="text-sm font-medium">{option.text}</p>
                          <button onClick={() => { removeFieldOption(field.id, option.id) }} className="text-red-600"><TrashIcon className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          }
        </div>
      </div>
    )
  },
  file: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, removeFieldById, setFieldRequired, updateFieldResponse, fieldResponses } = useFormContext();

    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || '') : '';
    const fileName = fieldResponse ? fieldResponse.split(':')[0] : '';
    const fileValue = fieldResponse ? fieldResponse.split(':')[1] : '';

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button onClick={() => { removeFieldById(field.id) }} className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"><TrashIcon className="w-4 h-4" /></button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary"><PaperclipIcon className="w-4 h-4" /></div>
              <p className="text-lg font-semibold">File</p>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch checked={field.required} onCheckedChange={(checked) => setFieldRequired(field.id, checked)} size="sm" id={`${field.id}-required`} />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <FileUploader disabled={disabled || !preview} fileName={fileName} fileValue={fileValue} onFileUpload={(file) => { updateFieldResponse(field.id, `${file.filename ? `${file.filename}:` : ''}${file.base64}`, field.type); }} />
      </div>
    )
  },
  image: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, removeFieldById, setFieldRequired, updateFieldResponse, fieldResponses } = useFormContext();

    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || '') : '';
    const fileName = fieldResponse ? fieldResponse.split(':')[0] : '';
    const fileValue = fieldResponse ? fieldResponse.split(':')[1] : '';

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button onClick={() => { removeFieldById(field.id) }} className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"><TrashIcon className="w-4 h-4" /></button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary"><ImageIcon className="w-4 h-4" /></div>
              <p className="text-lg font-semibold">Image</p>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch checked={field.required} onCheckedChange={(checked) => setFieldRequired(field.id, checked)} size="sm" id={`${field.id}-required`} />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <FileUploader disabled={disabled || !preview} allowedTypes={["image/png", "image/jpeg", "image/gif", "image/svg+xml"]} fileName={fileName} fileValue={fileValue} onFileUpload={(file) => { updateFieldResponse(field.id, `${file.filename ? `${file.filename}:` : ''}${file.base64}`, field.type); }} />
      </div>
    )
  },
  text: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, removeFieldById } = useFormContext();

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button onClick={() => { removeFieldById(field.id) }} className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"><TrashIcon className="w-4 h-4" /></button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary"><ClipboardTypeIcon className="w-4 h-4" /></div>
              <p className="text-lg font-semibold">Text content</p>
            </div>
          </div>
        }

        <Question field={field} preview={preview || disabled} setQuestionForField={setQuestionForField} />

      </div>
    )
  },
  spacer: () => <div className="w-full h-10 border-2 rounded-md border-primary min-w-64 bg-primary/10"></div>,
  number: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, setFieldRequired, removeFieldById, updateFieldResponse, fieldResponses } = useFormContext();

    // Use stored response only in preview mode, matching your existing pattern
    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || "") : "";

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button
                onClick={() => { removeFieldById(field.id) }}
                className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary">
                <HashIcon className="w-4 h-4" />
              </div>
              <p className="text-lg font-semibold">Number</p>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch
                checked={field.required}
                onCheckedChange={(checked) => setFieldRequired(field.id, checked)}
                size="sm"
                id={`${field.id}-required`}
              />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <Input
          type="number"
          inputMode="decimal"
          step="any"
          disabled={disabled || !preview}
          value={fieldResponse}
          // prevent scroll-wheel value changes when focused:
          onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
          onChange={(e) => {
            // store as string like your other fields (consistent with updateFieldResponse signature)
            updateFieldResponse(field.id, e.currentTarget.value, field.type);
          }}
        />
      </div>
    );
  },
  rating: ({ field, preview = false, disabled = false }: { field: FieldType, preview?: boolean, disabled?: boolean }) => {
    const { setQuestionForField, setFieldRequired, removeFieldById, updateFieldResponse, fieldResponses } = useFormContext();

    const fieldResponse = preview ? (fieldResponses.find((r) => r.fieldId === field.id)?.value || "") : "";

    const options = [
      { value: "1", label: "Never", emoji: "😴" },
      { value: "2", label: "Rarely", emoji: "😟" },
      { value: "3", label: "Sometimes", emoji: "😐" },
      { value: "4", label: "Often", emoji: "🙂" },
      { value: "5", label: "Always", emoji: "😁" },
    ];

    const isSelected = (v: string) => fieldResponse === v;

    const handleSelect = (v: string) => {
      if (!preview || disabled) return;
      updateFieldResponse(field.id, v, field.type);
    };

    return (
      <div className="flex flex-col gap-2 group">
        {!preview &&
          <div className="flex items-center justify-between pb-3 border-b">
            <div className="flex items-center gap-2">
              <button
                onClick={() => removeFieldById(field.id)}
                className="items-center justify-center hidden text-red-600 rounded-md group-hover:flex w-7 h-7 bg-red-600/10"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
              <div className="flex items-center justify-center rounded-md group-hover:hidden w-7 h-7 bg-primary/10 text-primary">
                <SmileIcon className="w-4 h-4" />
              </div>
              <p className="text-lg font-semibold">1–5 Picker</p>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${field.id}-required`}>Required</Label>
              <Switch
                checked={field.required}
                onCheckedChange={(checked) => setFieldRequired(field.id, checked)}
                size="sm"
                id={`${field.id}-required`}
              />
            </div>
          </div>
        }

        <Question field={field} preview={preview} setQuestionForField={setQuestionForField} />

        <div
          role="radiogroup"
          aria-disabled={disabled || !preview}
          className="grid grid-cols-5 gap-3"
        >
          {options.map(o => {
            const selected = isSelected(o.value);
            return (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled || !preview}
                onClick={() => handleSelect(o.value)}
                className={[
                  "group/tile select-none",
                  "relative flex h-24 w-full flex-col items-center justify-center gap-2", // ⬅️ relative for badge
                  "rounded-2xl border transition",
                  selected
                    ? "border-orange-400 ring-2 ring-orange-300/60 bg-orange-50"
                    : "border-neutral-200 bg-neutral-50 hover:bg-neutral-100",
                  (disabled || !preview)
                    ? "opacity-60 cursor-not-allowed"
                    : "cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-300",
                ].join(" ")}
              >
                {/* corner check badge */}
                {selected && (
                  <span
                    className="pointer-events-none absolute -top-2 -right-2 z-10
                 flex h-6 w-6 items-center justify-center rounded-full
                 bg-orange-500 text-white shadow ring-2 ring-white"
                    aria-hidden="true"
                  >
                    <CheckIcon className="w-3 h-3" />
                  </span>
                )}

                <span className="text-4xl sm:text-5xl leading-none">{o.emoji}</span>
                <span className={selected ? "text-sm font-semibold text-orange-800" : "text-sm font-medium text-neutral-700"}>
                  {o.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  },
};
